import axios from "axios";
import { tokenBlacklistService, validateTokenSecurity } from './tokenSecurity';
import { cacheControl } from '../middleware/cacheControl';
import { getSessionHeaders } from './userSessionManager';
import { validateSession } from '../hooks/useConcurrentLoginPrevention';

let timeoutCallbacks = null;

export const setTimeoutResetCallback = (callbacks) => {
    timeoutCallbacks = callbacks;
};


axios.interceptors.request.use(
    async config => {
        // Pause session timeout during API calls
        if (timeoutCallbacks?.pauseTimeout) {
            timeoutCallbacks.pauseTimeout();
        }
        
        const url = config.url || '';
        
        // Completely skip all validation for role/user endpoints
        const isRoleUserEndpoint = url.includes('/getroles') || 
                                 url.includes('/getusers') || 
                                 url.includes('/getactiveroles') ||
                                 url.includes('/addrole') ||
                                 url.includes('/adduser') ||
                                 url.includes('/updateuser') ||
                                 url.includes('/updateRole');
        
        if (isRoleUserEndpoint) {
            const token = sessionStorage.getItem('Token');
            if (token) {
                config.headers['Authorization'] = 'Bearer ' + token;
            }
            return config;
        }
        
        // Skip session validation for auth endpoints
        const skipSessionValidation = url.includes('/authenticate') || 
                                    url.includes('/validate-session');
        
        if (!skipSessionValidation) {
            const isSessionValid = await validateSession();
            if (!isSessionValid) {
                return Promise.reject(new Error('Session validation failed'));
            }
        }
        
        const token = sessionStorage.getItem('Token');
        if (token) {
            try {
                const validation = validateTokenSecurity(token);
                if (!validation.isValid) {
                    console.warn('Token validation failed:', validation.error);
                    sessionStorage.clear();
                    window.location.reload();
                    return Promise.reject(new Error('Invalid token'));
                }
                config.headers['Authorization'] = 'Bearer ' + token;
            } catch (error) {
                console.error('Token validation error:', error);
                return Promise.reject(new Error('Token validation failed'));
            }
        }
          
        // Only add session headers for non-authenticate endpoints
        if (!url.includes('/authenticate')) {
            Object.assign(config.headers, getSessionHeaders());
        }
        
        return config;
    },
    error => {
        Promise.reject(new Error(error));
    }
);


axios.interceptors.response.use((response) => { // block to handle success case
    // Resume session timeout after API completes
    if (timeoutCallbacks?.resumeTimeout) {
        timeoutCallbacks.resumeTimeout();
    }
    return response
}, function (error) { // block to handle error case
    // Resume session timeout after API error
    if (timeoutCallbacks?.resumeTimeout) {
        timeoutCallbacks.resumeTimeout();
    }
    console.log("Interceptor Error", error)
    console.log("Interceptor Error status", error.response?.status );

    // Don't reject 409 errors - let them pass through for concurrent session handling
    if (error.response?.status === 409) {
        return Promise.reject(error);
    }
    console.log("Interceptor: Adding to  no 409");
    return Promise.reject(error)
});

const originalFetch = window.fetch;
window.fetch = async function (...args) {
    let [url, options = {}] = args;
    
    console.log("inside interceptor---- fetch called with:", url, options  );
    
    // Skip interceptor for certain endpoints to avoid CORS issues and circular calls
    const skipValidation = typeof url === 'string' && (
        url.includes('/authenticate') ||
        url.includes('/validate-session') ||
        url.includes('/getroles') ||
        url.includes('/getusers') ||
        url.includes('/getactiveroles') ||
        url.includes('/addrole') ||
        url.includes('/adduser') ||
        url.includes('/updateuser') ||
        url.includes('/updateRole')
    );
    
    if (skipValidation) {
        return originalFetch(url, options);
    }
    
    // Validate session on other fetch calls
    const isSessionValid = await validateSession();
    if (!isSessionValid) {
        return Promise.reject(new Error('Session validation failed'));
    }
    
    // Pause session timeout during API calls
    if (timeoutCallbacks?.pauseTimeout) {
        timeoutCallbacks.pauseTimeout();
    }

    return originalFetch(url, options)
        .then(response => {
            // Resume session timeout after API completes
            if (timeoutCallbacks?.resumeTimeout) {
                timeoutCallbacks.resumeTimeout();
            }
            return response;
        })
        .catch(error => {
            // Resume session timeout after API error
            if (timeoutCallbacks?.resumeTimeout) {
                timeoutCallbacks.resumeTimeout();
            }
            return Promise.reject(error)

        });
};
